"use client";
import { signIn } from "next-auth/react";
import Link from "next/link";
import { useEffect, useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import { GoPersonFill } from "react-icons/go";
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
} from "~/components/shadcn/Avatar";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "~/components/shadcn/DropDownMenu";
import Spinner from "~/components/themed/spinner";
import { Button } from "~/components/v3/themed/Button";
import { useLogout } from "~/hooks/useLogout";
import { useUserStore } from "~/state/store";
import { api } from "~/utils/api";
import { cn } from "~/utils/fontsConfig";
import ThemedLink from "./ThemedLink";
import { Session } from "next-auth";
import { If } from "../Conditional";
import { LuBell, LuCalendar, LuSettings } from "react-icons/lu";
import { UserAvatar } from "~/components/v4/shadcn/UserAvatar";
import { slugifyUser } from "~/utils/string";
import { CustomizableLayout } from "~/types/CustomizableLayout";

interface Props {
    className?: string;
    onClick?: () => void;
    isMobile?: boolean;
    session?: Session | null;
    customization?: CustomizableLayout;
}

export const LoginControlButton = ({
    className,
    isMobile,
    onClick,
    session,
    customization,
}: Props) => {
    const { data: fullUser, isLoading } = api.user.getUserById.useQuery(
        {
            userId: session?.user?.id ?? "",
        },
        {
            enabled: !!session?.user?.id,
        },
    );

    const [editProfile, showEditProfile] = useState(false);
    const handleLogout = useLogout();
    const { user, setUser } = useUserStore((state) => ({
        user: state.user,
        setUser: state.setUser,
    }));

    useEffect(() => {
        const setUserStoreUser = async () => {
            if (session?.user?.id && user === null) {
                if (!fullUser) return;
                else setUser(fullUser);
            }
        };
        setUserStoreUser();

        if (!isLoading && (!fullUser?.name || !fullUser?.bio)) {
            showEditProfile(true);
        }
    }, [session?.user, setUser, isLoading, fullUser]);

    if (session === undefined) {
        return (
            <div className="flex h-full w-full items-center gap-3">
                <div className="h-5 w-32 animate-pulse rounded-md bg-v3-N-500" />
                <div className="h-10 w-10 animate-pulse rounded-full bg-v3-N-500" />
            </div>
        );
    } else if (session === null) {
        return (
            <div className={cn("flex flex-col gap-2 font-faktum", className)}>
                <div className="flex items-center gap-5">
                    <Button
                        onClick={() => {
                            signIn().catch(console.debug);
                            onClick?.();
                        }}
                        variant="secondaryLink"
                        className={cn(
                            "h-max p-0 text-base no-underline",
                            customization?.linkClassName,
                        )}
                    >
                        Log in
                    </Button>
                    <Button
                        onClick={() => {
                            signIn().catch(console.debug);
                            onClick?.();
                        }}
                        className={cn(customization?.buttons?.default)}
                    >
                        Sign Up
                    </Button>
                </div>
                {isMobile ? (
                    <ThemedLink
                        href="/signup"
                        className={cn(
                            "text-base text-white no-underline",
                            customization?.linkClassName,
                        )}
                        onClick={onClick}
                    >
                        Sign up
                    </ThemedLink>
                ) : (
                    <Button
                        onClick={() => {
                            signIn().catch(console.debug);
                            onClick?.();
                        }}
                        variant="secondary"
                        className={cn(
                            "h-max p-0 text-base no-underline md:hidden",
                            customization?.buttons?.secondary,
                        )}
                    ></Button>
                )}
            </div>
        );
    } else if (session?.user !== null) {
        return (
            <DropdownMenu modal={false}>
                <DropdownMenuTrigger>
                    <div className="flex flex-row items-center gap-3">
                        <UserAvatar user={session.user} className="h-10 w-10" />
                        <FaChevronDown
                            className="h-3 w-3 text-white"
                            aria-hidden="true"
                        />
                    </div>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end">
                    <DropdownMenuLabel asChild>
                        <div className="flex flex-col gap-2">
                            <div className="flex flex-row items-center gap-2">
                                <UserAvatar
                                    user={session.user}
                                    className="h-8 w-8"
                                />
                                <div className="flex flex-col">
                                    <span>{session?.user?.name}</span>
                                    <span className="text-xs font-normal text-v3-N-300">
                                        {session?.user?.email}
                                    </span>
                                </div>
                            </div>

                            {editProfile ? (
                                <Link
                                    href={`/profile/${slugifyUser(user ?? session.user)}?editMode=true`}
                                >
                                    <span className="text-secondary">
                                        Update Profile
                                    </span>
                                </Link>
                            ) : (
                                <Link
                                    className="text-xs text-v3-N-300 hover:underline"
                                    href={`/profile/${slugifyUser(user ?? session.user)}`}
                                >
                                    View my profile
                                </Link>
                            )}
                        </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />

                    <DropdownMenuItem className="cursor-pointer py-2" asChild>
                        <Link href="/preferences" className="flex gap-2">
                            <LuSettings className="h-4 w-4" />
                            Settings
                        </Link>
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={() => {
                            handleLogout();
                        }}
                    >
                        Log out
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        );
    } else return <Spinner type="small" />;
};
